<!--
@component
PhotoCarousel

See markup below.
-->

<script lang="ts">
  import { Splide, SplideSlide } from '@splidejs/svelte-splide';

  interface Photo {
    src: string;
    altText: string;
    credit?: string;
  }

  let {
    photos = [],
    maxHeight = 600,
    width = 'normal',
    preloadImages = 2,
    class: className = '',
  }: {
    photos: Photo[];
    maxHeight?: number;
    width?: string;
    preloadImages?: number;
    class?: string;
  } = $props();

  const splideOptions = {
    type: 'loop',
    perPage: 1,
    perMove: 1,
    gap: '1rem',
    arrows: true,
    pagination: true,
    keyboard: true,
    preloadPages: preloadImages,
    lazyLoad: 'nearby' as const,
  };
</script>

<div class="photo-carousel {className}" class:wide={width === 'wide'}>
  <Splide options={splideOptions} aria-label="Photo gallery">
    {#each photos as photo, i (photo.src)}
      <SplideSlide>
        <div class="slide-content" style="max-height: {maxHeight}px;">
          <img
            src={photo.src}
            alt={photo.altText}
            loading={i < preloadImages ? 'eager' : 'lazy'}
          />
          {#if photo.credit}
            <div class="photo-credit">{photo.credit}</div>
          {/if}
        </div>
      </SplideSlide>
    {/each}
  </Splide>
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  .photo-carousel {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;

    &.wide {
      max-width: 100%;
    }

    :global {
      .splide__slide {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .slide-content {
      position: relative;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    img {
      width: 100%;
      height: auto;
      max-height: 100%;
      object-fit: contain;
      display: block;
    }

    .photo-credit {
      margin-top: 0.5rem;
      @include mixins.text-sm;
      color: var(--theme-colour-text-secondary);
      text-align: right;
      width: 100%;
      padding: 0 1rem;
    }

    :global {
      .splide__arrow {
        background: var(--theme-style-overlay-dark-colour);
        border: none;
        opacity: 0.7;
        transition: opacity 0.2s;

        &:hover {
          opacity: 1;
        }

        svg {
          fill: var(--theme-colour-white);
        }
      }

      .splide__pagination {
        bottom: -2rem;
      }

      .splide__pagination__page {
        background: var(--theme-colour-mid-grey);

        &.is-active {
          background: var(--theme-colour-text-primary);
        }
      }
    }
  }
</style>
